import { Badge, LinkButton } from '@/ui';

import type { TodayRow, TodaySectionModel } from './todayModel';

interface WorkRowProps {
  readonly row: TodayRow;
  /** The id of the sentence, so the action can be described by it. */
  readonly sentenceId: string;
}

function WorkRow({ row, sentenceId }: WorkRowProps): React.JSX.Element {
  return (
    <li className="fo-today-row">
      <p className="fo-today-row__sentence" id={sentenceId}>
        {row.subject === '' ? null : (
          <>
            <strong>{row.subject}</strong>
            {': '}
          </>
        )}
        {row.predicate}
      </p>
      {/* Described by its sentence, so a list of links still says which row each one is for. */}
      <LinkButton to={row.to} aria-describedby={sentenceId}>
        {row.actionLabel}
      </LinkButton>
    </li>
  );
}

export interface TodaySectionProps {
  readonly section: TodaySectionModel;
}

/**
 * One kind of work waiting: a heading with how many, up to five rows, and the way to the rest.
 *
 * Each row is a sentence and the one action that deals with it. "See all" carries the section in
 * its name, because a page of sections would otherwise offer a list of links that all say the same.
 *
 * @param props see {@link TodaySectionProps}
 * @returns the section
 */
export function TodaySection({ section }: TodaySectionProps): React.JSX.Element {
  const headingId = `today-${section.key.toLowerCase()}`;
  return (
    <section className="fo-today-section" aria-labelledby={headingId}>
      <header className="fo-today-section__header">
        <h2 className="fo-today-section__title" id={headingId}>
          {section.title} <Badge>{section.count}</Badge>
        </h2>
        {section.seeAll === null ? null : (
          <LinkButton variant="ghost" to={section.seeAll.to} aria-label={section.seeAll.label}>
            See all
          </LinkButton>
        )}
      </header>
      <ul className="fo-today-section__rows">
        {section.rows.map((row, index) => (
          <WorkRow key={row.id} row={row} sentenceId={`${headingId}-row-${String(index)}`} />
        ))}
      </ul>
    </section>
  );
}
